import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SERVICES_CATALOG } from '@/lib/servicesCatalog';
import { ServiceOrderCard } from '@/components/OrderServiceCTA';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { authFetch } from '@/lib/auth';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

export default function OrderHub() {
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Get recommended service from navigation state (e.g., from service page)
  const recommendedService = location.state?.recommendedService || null;
  
  // Fetch draft orders summary on mount
  useEffect(() => {
    const fetchDrafts = async () => {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }
      
      // If not authenticated, skip draft fetch
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await authFetch(`${API_URL}/api/orders/drafts/summary`, {
          headers: {
            'Content-Type': 'application/json'
          }
        }, false);
        
        if (response.ok) {
          const data = await response.json();
          setDrafts(data.drafts || {});
        }
      } catch (error) {
        console.error('Failed to fetch drafts summary:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDrafts();
  }, [isAuthenticated, authLoading]);
  
  // Count total drafts for display
  const totalDrafts = Object.values(drafts).reduce((sum, d) => sum + (d.count || 0), 0);
  
  return (
    <div className="flex flex-col min-h-screen bg-[#0f1724]">
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-20">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          {/* Eyebrow */}
          <p className="text-[#C9A962] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
            Order Services
          </p>
          
          {/* Headline */}
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white tracking-wide mb-6">
            {totalDrafts > 0 ? "Continue Your Order" : "Choose Your Service"}
          </h1>
          
          {/* Subheadline */}
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
            {totalDrafts > 0 
              ? `You have ${totalDrafts} draft order${totalDrafts > 1 ? 's' : ''} in progress. Pick up where you left off or start a new order.`
              : "Select from our professional creative services. Each order includes personalized attention and premium results."
            }
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-20 md:pb-32">
        <div className="container mx-auto px-6 max-w-5xl">
          {(loading || authLoading) ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#C9A962]" />
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {SERVICES_CATALOG.map((service) => (
                <ServiceOrderCard 
                  key={service.key} 
                  service={service} 
                  variant="dark"
                  draftInfo={drafts[service.key] || null}
                  isHighlighted={recommendedService === service.key}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Help Section */}
      <section className="pb-20 md:pb-32">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <div className="bg-[#141c2b] rounded-2xl border border-white/10 p-8 md:p-12">
            <h2 className="font-serif text-2xl md:text-3xl text-white mb-4">
              Not Sure Which Service You Need?
            </h2>
            <p className="text-white/60 mb-6 leading-relaxed">
              We're happy to help you determine the best approach for your project. 
              Reach out and we'll provide a personalized recommendation.
            </p>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-2 text-[#C9A962] font-medium hover:gap-3 transition-all"
            >
              <span>Contact Us</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Existing Orders Link */}
      <section className="pb-20">
        <div className="container mx-auto px-6 text-center">
          <p className="text-white/40 text-sm">
            Already have an order?{' '}
            <Link to="/orders" className="text-[#C9A962] hover:underline">
              View your orders
            </Link>
          </p>
        </div>
      </section>

      {/* Footer gradient transition */}
      <div className="h-16 bg-gradient-to-b from-[#0f1724] to-[#050816]"></div>
    </div>
  );
}
